import { useQuery } from "@tanstack/react-query"
import type * as React from "react"

import {
  DetectorProfileList,
  detectorProfileOptionsQuery,
  type DetectorProfile,
  type DetectorProfileResolved,
} from "@/components/domain/detector-profile"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"

export interface ReviewSettingsPopoverProps {
  /** What the run applied. Null until it has resolved the settings. */
  resolved: DetectorProfileResolved | null
  /** What was asked for, shown until the run resolves it. */
  requested: DetectorProfile
  children: React.ReactNode
}

/**
 * The review's detector settings, read-only: each one's applied value and
 * where it came from — set on this review, the engagement's profile, or the
 * deployment default.
 *
 * Read-only on purpose: changing a setting changes what the review checks, so
 * it is a new run rather than an edit, and the place for that is the intake.
 */
export function ReviewSettingsPopover({
  resolved,
  requested,
  children,
}: ReviewSettingsPopoverProps): JSX.Element {
  const options = useQuery(detectorProfileOptionsQuery())

  return (
    <Popover>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align="end" sideOffset={8} className="w-[360px] p-0">
        <header className="border-b border-border-soft px-4 py-3">
          <p className="text-body-sm font-semibold text-text">Settings</p>
          <p className="mt-0.5 text-caption text-text-3">
            {resolved
              ? "What this review checked with, and where each setting came from."
              : "What was asked for. Automatic is decided when the review runs."}
          </p>
        </header>
        <div className="px-4 py-3">
          {options.isError ? (
            <p className="text-caption text-text-3">The setting labels could not be loaded.</p>
          ) : (
            <DetectorProfileList
              options={options.data}
              resolved={resolved}
              requested={requested}
              showSource
            />
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
